import { useQuery } from "@tanstack/react-query";
import { getAssignmentAppsOptions } from "@/lib/data/react-query/matters/matter-task";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import { useUser } from "@/components/UserProvider";
import {
  ActionConfirmationModal,
  Badge,
  Calendar,
  ContextMenu,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  PriorityIcon,
  SearchableContextMenuSubmenu,
  TaskStatusIcon,
} from "@/components/ui";
import type { PriorityLevel } from "@/components/ui/Icons/PriorityIcon";
import {
  useAddMatterTaskUserAssignments,
  useDeleteMatterTasks,
  useRemoveMatterTaskUserAssignments,
  useUpdateMatterTasks,
} from "@/lib/data/react-query";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import { MATTER_TASK_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { formatUsersName } from "@/lib/utils/format";
import {
  Calendar as CalendarIcon,
  ListChecks,
  UserRound,
  UserRoundMinus,
  UserRoundPlus,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import type { MatterTaskWithType } from "./matter-task-columns";
import type { MatterAssignee } from "@/lib/types";
import {
  buildTaskUserOptions,
  getMatterRoleLabelsByUserId,
  type TaskUserOption,
} from "./task-user-options";

const TASK_STATUS_ICON_BY_STATUS = {
  Open: "open",
  "In Progress": "in-progress",
  Completed: "completed",
} as const;

type TaskAssignmentRole = "assignees" | "assigners";
type TaskAssignmentUser = NonNullable<
  MatterTaskWithType[TaskAssignmentRole]
>[number];

const getAssignedUsers = (
  tasks: MatterTaskWithType[],
  role: TaskAssignmentRole,
) => {
  const usersById = new Map<string, TaskAssignmentUser>();

  tasks.forEach((task) => {
    task[role]?.forEach((assignment) => {
      if (assignment.userId) {
        usersById.set(assignment.userId, assignment);
      }
    });
  });

  return [...usersById.values()];
};

interface MatterTaskContextMenuProps {
  massTortId?: string;
  matterAssignees?: MatterAssignee[];
  matterId: string;
  onEdit: () => void;
  task: MatterTaskWithType;
  tasksToEdit: MatterTaskWithType[];
  trigger: ReactNode;
}

export const MatterTaskContextMenu = ({
  massTortId,
  matterId,
  matterAssignees = [],
  onEdit,
  task,
  tasksToEdit,
  trigger,
}: MatterTaskContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const addMatterTaskUserAssignments = useAddMatterTaskUserAssignments();
  const deleteMatterTasks = useDeleteMatterTasks();
  const removeMatterTaskUserAssignments = useRemoveMatterTaskUserAssignments();
  const updateMatterTasks = useUpdateMatterTasks();
  const [removeAssigneeSearch, setRemoveAssigneeSearch] = useState("");
  const [removeAssignerSearch, setRemoveAssignerSearch] = useState("");
  const [isAddAssigneeMenuOpen, setIsAddAssigneeMenuOpen] = useState(false);
  const [isAddAssignerMenuOpen, setIsAddAssignerMenuOpen] = useState(false);
  const [contextMenuKey, setContextMenuKey] = useState(0);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const taskParentPermissionKey = massTortId
    ? ACTION_PERMISSION_KEYS.massTort
    : ACTION_PERMISSION_KEYS.matter;
  const canEdit = hasActionPermission(
    taskParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    taskParentPermissionKey,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canAssignUsers = canEdit && !massTortId;
  const { data: assignmentApps = [] } = useQuery(
    getAssignmentAppsOptions(canAssignUsers),
  );
  const assignedApps = [
    ...new Map(
      tasksToEdit
        .flatMap((selected) => selected.assignees ?? [])
        .flatMap((assignment) => {
          const integrationAppId = assignment.integrationAppId;
          return integrationAppId
            ? [[integrationAppId, { ...assignment, integrationAppId }] as const]
            : [];
        }),
    ).values(),
  ];
  const assigneeDirectory = useUserDirectory({
    enabled: canAssignUsers,
    open: isAddAssigneeMenuOpen,
  });
  const assignerDirectory = useUserDirectory({
    enabled: canAssignUsers,
    open: isAddAssignerMenuOpen,
  });
  const { search: addAssigneeSearch, setSearch: setAddAssigneeSearch } =
    assigneeDirectory;
  const { search: addAssignerSearch, setSearch: setAddAssignerSearch } =
    assignerDirectory;
  const copyValue = [task.title, task.description].filter(Boolean).join("\n");
  const isUpdating = updateMatterTasks.isPending;
  const matterRoleLabelsByUserId = getMatterRoleLabelsByUserId(matterAssignees);
  const getAssignmentRoleLabels = (assignment: TaskAssignmentUser) =>
    assignment.userId
      ? (matterRoleLabelsByUserId.get(assignment.userId) ?? [])
      : [];
  const filterAssignedUsers = (
    assignments: TaskAssignmentUser[],
    search: string,
  ) => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return assignments;
    }

    return assignments.filter((assignment) =>
      `${formatUsersName(assignment)} ${assignment.email} ${getAssignmentRoleLabels(assignment).join(" ")}`
        .toLowerCase()
        .includes(normalizedSearch),
    );
  };
  const assigneeUsers = getAssignedUsers(tasksToEdit, "assignees");
  const assignerUsers = getAssignedUsers(tasksToEdit, "assigners");
  const addAssigneeUsers = buildTaskUserOptions(
    matterAssignees,
    assigneeDirectory.users,
    addAssigneeSearch,
  );
  const addAssignerUsers = buildTaskUserOptions(
    matterAssignees,
    assignerDirectory.users,
    addAssignerSearch,
  );
  const removeAssigneeUsers = filterAssignedUsers(
    assigneeUsers,
    removeAssigneeSearch,
  );
  const removeAssignerUsers = filterAssignedUsers(
    assignerUsers,
    removeAssignerSearch,
  );

  const assignedUserIds = (role: "assignees" | "assigners") => {
    const firstTaskAssignments = tasksToEdit[0]?.[role] ?? [];
    return firstTaskAssignments.flatMap((assignment) => {
      if (!assignment.userId) {
        return [];
      }

      return tasksToEdit.every((taskToEdit) =>
        taskToEdit[role]?.some(
          (taskAssignment) => taskAssignment.userId === assignment.userId,
        ),
      )
        ? [assignment.userId]
        : [];
    });
  };
  const assigneeUserIds = assignedUserIds("assignees");
  const assignerUserIds = assignedUserIds("assigners");
  const selectedPriority = tasksToEdit.every(
    (taskToEdit) => taskToEdit.priority === task.priority,
  )
    ? task.priority
    : undefined;
  const selectedStatus = tasksToEdit.every(
    (taskToEdit) => taskToEdit.status === task.status,
  )
    ? task.status
    : undefined;
  const renderAddUser = (user: TaskUserOption) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <span>{formatUsersName(user) || user.email}</span>
      {user.roleLabels.map((role) => (
        <Badge key={role}>{role}</Badge>
      ))}
    </div>
  );
  const renderRemoveUser = (assignment: TaskAssignmentUser) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <span>{formatUsersName(assignment) || assignment.email}</span>
      {getAssignmentRoleLabels(assignment).map((role) => (
        <Badge key={role}>{role}</Badge>
      ))}
    </div>
  );

  const updateTasks = async (
    update: Partial<
      Pick<MatterTaskWithType, "priority" | "status" | "startDate" | "dueDate">
    >,
  ) => {
    await updateMatterTasks.mutateAsync(
      tasksToEdit.map((taskToUpdate) => ({
        ...(massTortId ? { massTortId } : { matterId: taskToUpdate.matterId }),
        id: taskToUpdate.id,
        dateModified: taskToUpdate.dateModified,
        ...update,
      })),
    );
  };

  return (
    <>
      <ContextMenu
        key={contextMenuKey}
        entityName="task"
        open={canEdit ? { onSelect: onEdit } : false}
        copy={{ value: copyValue, successLabel: "Task details" }}
        contentClassName="min-w-[220px]"
        delete={
          canDelete
            ? {
                disabled: deleteMatterTasks.isPending,
                onSelect: () => {
                  setIsDeleteConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={trigger}
      >
        {canEdit ? (
          <>
            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={isUpdating}>
                <PriorityIcon level="medium" />
                Priority
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {MATTER_TASK_PRIORITY_OPTIONS.map((priority) => (
                  <ContextMenuItem
                    key={priority}
                    selected={priority === selectedPriority}
                    onSelect={() => {
                      void updateTasks({ priority });
                    }}
                  >
                    <PriorityIcon
                      level={priority.toLowerCase() as PriorityLevel}
                    />
                    {priority}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
            {canAssignUsers && (
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <UserRound />
                  App assignees
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  {assignmentApps.map((app) => (
                    <ContextMenuItem
                      key={app.integrationAppId}
                      disabled={addMatterTaskUserAssignments.isPending}
                      onSelect={() => {
                        void addMatterTaskUserAssignments.mutateAsync({
                          role: "Assignee",
                          taskIds: tasksToEdit.map((selected) => selected.id),
                          integrationAppId: app.integrationAppId,
                        });
                      }}
                    >
                      Add {app.integrationAppName ?? "app"}
                    </ContextMenuItem>
                  ))}
                  {assignedApps.map((app) => (
                    <ContextMenuItem
                      key={`remove:${app.integrationAppId}`}
                      disabled={removeMatterTaskUserAssignments.isPending}
                      onSelect={() => {
                        void removeMatterTaskUserAssignments.mutateAsync({
                          role: "Assignee",
                          taskIds: tasksToEdit.map((selected) => selected.id),
                          integrationAppId: app.integrationAppId,
                        });
                      }}
                    >
                      Remove {app.integrationAppName ?? "unavailable app"}
                    </ContextMenuItem>
                  ))}
                  {!assignmentApps.length && !assignedApps.length && (
                    <ContextMenuItem disabled>
                      No installed apps
                    </ContextMenuItem>
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>
            )}
            {canAssignUsers ? (
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <UserRound />
                  Assigners
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  <SearchableContextMenuSubmenu
                    emptyMessage="No users found."
                    getItemGroup={(user) => user.group}
                    getItemKey={(user) => user.id}
                    isLoading={
                      assignerDirectory.isLoading ||
                      addMatterTaskUserAssignments.isPending
                    }
                    items={addAssignerUsers}
                    hasNextPage={assignerDirectory.hasNextPage}
                    isFetchingNextPage={assignerDirectory.isFetchingNextPage}
                    onLoadMore={assignerDirectory.selectProps.onLoadMore}
                    footer={
                      assignerDirectory.isLoading ||
                      assignerDirectory.isError ||
                      assignerDirectory.hasNextPage ||
                      assignerDirectory.isFetchingNextPage ? (
                        <AsyncSelectOptions
                          {...assignerDirectory.selectProps}
                        />
                      ) : undefined
                    }
                    onOpenChange={(open) => {
                      setIsAddAssignerMenuOpen(open);
                      if (!open) setAddAssignerSearch("");
                    }}
                    onSearchChange={setAddAssignerSearch}
                    onSelectItem={(user) => {
                      void addMatterTaskUserAssignments.mutateAsync({
                        role: "Assigner",
                        taskIds: tasksToEdit.map((taskToEdit) => taskToEdit.id),
                        userId: user.id,
                      });
                    }}
                    placeholder="Search users..."
                    renderItem={renderAddUser}
                    search={addAssignerSearch}
                    selectedItemKeys={assignerUserIds}
                    title={
                      <>
                        <UserRoundPlus />
                        Add
                      </>
                    }
                  />
                  <SearchableContextMenuSubmenu
                    emptyMessage="No assigners found."
                    getItemKey={(assignment) =>
                      assignment.userId ?? assignment.id
                    }
                    isLoading={removeMatterTaskUserAssignments.isPending}
                    items={removeAssignerUsers}
                    onOpenChange={(open) => {
                      if (!open) setRemoveAssignerSearch("");
                    }}
                    onSearchChange={setRemoveAssignerSearch}
                    onSelectItem={(assignment) => {
                      if (!assignment.userId) return;

                      void removeMatterTaskUserAssignments.mutateAsync({
                        role: "Assigner",
                        taskIds: tasksToEdit.map((taskToEdit) => taskToEdit.id),
                        userId: assignment.userId,
                      });
                    }}
                    placeholder="Search assigners..."
                    renderItem={renderRemoveUser}
                    search={removeAssignerSearch}
                    title={
                      <>
                        <UserRoundMinus />
                        Remove
                      </>
                    }
                  />
                </ContextMenuSubContent>
              </ContextMenuSub>
            ) : null}
            {canAssignUsers ? (
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <UserRound />
                  Assignees
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  <SearchableContextMenuSubmenu
                    emptyMessage="No users found."
                    getItemGroup={(user) => user.group}
                    getItemKey={(user) => user.id}
                    isLoading={
                      assigneeDirectory.isLoading ||
                      addMatterTaskUserAssignments.isPending
                    }
                    items={addAssigneeUsers}
                    hasNextPage={assigneeDirectory.hasNextPage}
                    isFetchingNextPage={assigneeDirectory.isFetchingNextPage}
                    onLoadMore={assigneeDirectory.selectProps.onLoadMore}
                    footer={
                      assigneeDirectory.isLoading ||
                      assigneeDirectory.isError ||
                      assigneeDirectory.hasNextPage ||
                      assigneeDirectory.isFetchingNextPage ? (
                        <AsyncSelectOptions
                          {...assigneeDirectory.selectProps}
                        />
                      ) : undefined
                    }
                    onOpenChange={(open) => {
                      setIsAddAssigneeMenuOpen(open);
                      if (!open) setAddAssigneeSearch("");
                    }}
                    onSearchChange={setAddAssigneeSearch}
                    onSelectItem={(user) => {
                      void addMatterTaskUserAssignments.mutateAsync({
                        role: "Assignee",
                        taskIds: tasksToEdit.map((taskToEdit) => taskToEdit.id),
                        userId: user.id,
                      });
                    }}
                    placeholder="Search users..."
                    renderItem={renderAddUser}
                    search={addAssigneeSearch}
                    selectedItemKeys={assigneeUserIds}
                    title={
                      <>
                        <UserRoundPlus />
                        Add
                      </>
                    }
                  />
                  <SearchableContextMenuSubmenu
                    emptyMessage="No assignees found."
                    getItemKey={(assignment) =>
                      assignment.userId ?? assignment.id
                    }
                    isLoading={removeMatterTaskUserAssignments.isPending}
                    items={removeAssigneeUsers}
                    onOpenChange={(open) => {
                      if (!open) setRemoveAssigneeSearch("");
                    }}
                    onSearchChange={setRemoveAssigneeSearch}
                    onSelectItem={(assignment) => {
                      if (!assignment.userId) return;

                      void removeMatterTaskUserAssignments.mutateAsync({
                        role: "Assignee",
                        taskIds: tasksToEdit.map((taskToEdit) => taskToEdit.id),
                        userId: assignment.userId,
                      });
                    }}
                    placeholder="Search assignees..."
                    renderItem={renderRemoveUser}
                    search={removeAssigneeSearch}
                    title={
                      <>
                        <UserRoundMinus />
                        Remove
                      </>
                    }
                  />
                </ContextMenuSubContent>
              </ContextMenuSub>
            ) : null}
            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={isUpdating}>
                <ListChecks />
                Status
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {MATTER_TASK_STATUS_OPTIONS.map((status) => (
                  <ContextMenuItem
                    key={status}
                    selected={status === selectedStatus}
                    onSelect={() => {
                      void updateTasks({ status });
                    }}
                  >
                    <TaskStatusIcon
                      status={TASK_STATUS_ICON_BY_STATUS[status]}
                    />
                    {status}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={isUpdating}>
                <CalendarIcon />
                Start date
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                <div className="p-2">
                  <Calendar
                    onChange={(startDate) => {
                      void updateTasks({ startDate: startDate.toString() });
                      setContextMenuKey((key) => key + 1);
                    }}
                  />
                </div>
              </ContextMenuSubContent>
            </ContextMenuSub>
            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={isUpdating}>
                <CalendarIcon />
                Due date
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                <div className="p-2">
                  <Calendar
                    onChange={(dueDate) => {
                      void updateTasks({ dueDate: dueDate.toString() });
                      setContextMenuKey((key) => key + 1);
                    }}
                  />
                </div>
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        ) : null}
        {!massTortId ? (
          <MatterSubItemEventLogsContextMenu
            onOpenEventLogs={() => {
              setIsEventLogsOpen(true);
            }}
          />
        ) : null}
      </ContextMenu>
      {!massTortId ? (
        <MatterSubItemEventLogsModal
          entityLabel={task.title}
          matter={{ id: matterId }}
          onOpenChange={setIsEventLogsOpen}
          open={isEventLogsOpen}
          subItem={{ id: task.id, type: "task" }}
        />
      ) : null}
      <ActionConfirmationModal
        open={isDeleteConfirmationOpen}
        setOpen={setIsDeleteConfirmationOpen}
        title={`Are you sure you want to delete ${
          tasksToEdit.length === 1
            ? `"${task.title}"`
            : `${tasksToEdit.length} tasks`
        }?`}
        description="This action cannot be undone."
        buttonText="Delete task"
        afterActionFn={async () => {
          await deleteMatterTasks.mutateAsync(
            tasksToEdit.map((taskToDelete) => ({
              ...(massTortId
                ? { massTortId }
                : { matterId: taskToDelete.matterId }),
              id: taskToDelete.id,
            })),
          );
        }}
        isActionPending={deleteMatterTasks.isPending}
      />
    </>
  );
};
